'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import * as React from 'react';
import type { CommentReply } from '@/lib/api/comments/models';
import type { RelatedBundle, RelatedEvent, RelatedNews } from '@/lib/api/event/models';
import { parseGifMarker } from '@/lib/components/Comments/gif';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { RelativeDateText } from '@/lib/components/Text/RelativeDateText';
import { Link } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { formatNumber } from '@/lib/utils/formatNumber';
import { newsSectionPathSlug } from '@/lib/utils/newsSection';

const SECTION_INTERVAL_MS = 4000;
/** Same pace in every section, whatever the rows are. Slow enough to read a row
 *  in passing, still moving visibly within a section's few seconds. */
const SCROLL_SPEED_PX_PER_S = 32;

interface EventBannerRelatedProps {
  related?: RelatedBundle;
  /** Shown when the event has no related content at all. */
  description?: string;
  className?: string;
}

/**
 * Cycles the featured slide's related events → comments → news on a timer,
 * skipping sections the backend returned empty.
 */
function EventBannerRelated({ related, description, className }: EventBannerRelatedProps) {
  // Row spacing per design: 8px between news cards, 4px elsewhere. The matching
  // padding is the seam between the two loop copies — being part of the copy's
  // own height, it saves the shift any arithmetic.
  const sections = React.useMemo(() => {
    const comments = (related?.comments ?? []).filter(
      (c) => !c.isDeleted && !parseGifMarker(c.content),
    );
    return [
      {
        key: 'events',
        spacing: 'gap-1 pb-1',
        rows: (related?.events ?? []).map((item) => <EventRow key={item.id} item={item} />),
      },
      {
        key: 'comments',
        spacing: 'gap-1 pb-1',
        rows: comments.map((item) => <CommentRow key={item.id} item={item} />),
      },
      {
        key: 'news',
        spacing: 'gap-2 pb-2',
        rows: (related?.news ?? []).map((item) => <NewsRow key={item.id} item={item} />),
      },
    ].filter((section) => section.rows.length > 0);
  }, [related]);

  const [index, setIndex] = React.useState(0);
  const [hovered, setHovered] = React.useState(false);

  // A new related bundle starts over from the first section.
  React.useEffect(() => {
    if (sections.length) setIndex(0);
  }, [sections.length]);

  React.useEffect(() => {
    if (hovered || sections.length < 2) return;
    const id = setInterval(
      () => setIndex((prev) => (prev + 1) % sections.length),
      SECTION_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, [hovered, sections.length]);

  const active = sections[Math.min(index, sections.length - 1)];
  const activeKey = active?.key;

  const boxRef = React.useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = React.useState({ key: '', shift: 0 });

  // How far one loop travels, and only for the section it was taken on — rows
  // of the next one never inherit it. Zero when they already fit the box: the
  // second copy would then just read as a duplicated list, so it isn't rendered.
  const shift = measured.key === activeKey ? measured.shift : 0;

  // A ResizeObserver rather than the ref itself, whose first call lands before
  // the container's own ref is even attached — too early to compare heights.
  const measure = React.useCallback(
    (copy: HTMLDivElement | null) => {
      if (!copy || !activeKey) return;
      const observer = new ResizeObserver(() => {
        const box = boxRef.current;
        if (box) {
          setMeasured({
            key: activeKey,
            shift: copy.offsetHeight > box.clientHeight ? copy.offsetHeight : 0,
          });
        }
      });
      observer.observe(copy);
      return () => observer.disconnect();
    },
    [activeKey],
  );

  // Fills the leftover column height (basis 0 + min-h-0, so content can never
  // stretch it) and clips the loop — the slide height stays put whichever
  // section is on screen. Mobile has no chart column, hence the fixed box.
  const containerCls = cn(
    // Mouse scroll is off; reduced motion stops the loop, so it gets it back.
    'group scrollbar-hide relative h-[190px] overflow-hidden motion-reduce:overflow-y-auto',
    'min-[900px]:h-0 min-[900px]:min-h-0 min-[900px]:flex-1',
    className,
  );

  // Fade the clipped rows out at both edges of the scroll area (design uses a
  // 40-60px gradient; 48px sits in the middle).
  const fadeCls =
    '[mask-image:linear-gradient(to_bottom,transparent,#000_48px,#000_calc(100%-48px),transparent)]';

  if (!active) {
    return (
      <p
        className={cn(
          containerCls,
          // Nothing loops here, so a long description keeps its scrollbar.
          'overflow-y-auto text-[13px] leading-[1.5] text-v2-text-primary opacity-50',
        )}
      >
        {description ?? ''}
      </p>
    );
  }

  const rowsCls = cn('flex shrink-0 flex-col', active.spacing);

  return (
    <div
      ref={boxRef}
      className={cn(containerCls, fadeCls)}
      onClick={stopSlideClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={active.key}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Own wrapper so the loop keyframes don't fight motion's transform. */}
          <div
            className={cn(
              'flex flex-col',
              shift > 0 && '[animation:banner-related-loop_var(--loop-duration)_linear_infinite]',
              'group-hover:[animation-play-state:paused] motion-reduce:[animation:none]',
            )}
            style={
              {
                '--loop-shift': `-${shift}px`,
                '--loop-duration': `${shift / SCROLL_SPEED_PX_PER_S}s`,
              } as React.CSSProperties
            }
          >
            <div ref={measure} className={rowsCls}>
              {active.rows}
            </div>
            {shift > 0 && (
              // Duplicate rows: hidden from a11y and unreachable by keyboard.
              <div aria-hidden inert className={rowsCls}>
                {active.rows}
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

const rowCls = 'flex shrink-0 flex-col gap-1 py-2';
const titleCls =
  'line-clamp-2 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary';
const metaCls =
  'flex flex-wrap items-center gap-1 text-[12px] leading-4 tracking-[-0.12px] text-v2-text-secondary';

// Rows link to their own target, so they opt out of the slide-wide click handler.
const stopSlideClick = (e: React.MouseEvent) => e.stopPropagation();

function Dot() {
  return <span aria-hidden className="size-[3px] shrink-0 rounded-full bg-current" />;
}

function EventRow({ item }: { item: RelatedEvent }) {
  // Categories and tags overlap ("Politics" / "politics"), so dedupe by name.
  const chips = [
    ...new Map(
      [...item.categories, ...item.tags].map((c) => [c.name.toLowerCase(), c.name]),
    ).values(),
  ].slice(0, 3);

  return (
    <Link
      href={`/event/${item.slug}`}
      data-banner-action
      onClick={stopSlideClick}
      className={cn(rowCls, 'group')}
    >
      {chips.length > 0 && (
        <div className={metaCls}>
          {chips.map((chip, i) => (
            <React.Fragment key={chip}>
              {i > 0 && <Dot />}
              <span className="whitespace-nowrap">{chip}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <span className={cn(titleCls, 'transition-colors group-hover:opacity-80')}>{item.title}</span>
      <div className={cn(metaCls, 'text-[10px] opacity-50')}>
        <RelativeDateText date={new Date(item.createdAt)} formatStyle="compact" />
        {item.volume ? (
          <>
            <Dot />
            <span className="whitespace-nowrap">${formatNumber(item.volume)} Vol.</span>
          </>
        ) : null}
      </div>
    </Link>
  );
}

function NewsRow({ item }: { item: RelatedNews }) {
  const locale = useLocale();
  const tNews = useTranslations('news');
  const tMarket = useTranslations('market');
  const isHot = item.tags.some((tag) => tag.toLowerCase().includes('hot'));

  return (
    // News lives in another zone — soft routing can't reach it.
    <a
      href={`/${locale}/news/${newsSectionPathSlug(item.category?.slug)}/${item.slug}`}
      data-banner-action
      onClick={stopSlideClick}
      className={cn(rowCls, 'group')}
    >
      <div className={metaCls}>
        {isHot && (
          <span className="me-1 inline-flex shrink-0 items-center gap-0.5 rounded-[4px] bg-[#E13736] px-1 py-0.5 text-[#F4F4F4]">
            <Icon name="Fire" size={12} />
            <span className="whitespace-nowrap text-[12px] leading-4">{tMarket('hottest')}</span>
          </span>
        )}
        <RelativeDateText date={new Date(item.publishedAt)} formatStyle="compact" />
        {item.commentCount > 0 && (
          <>
            <Dot />
            <span className="whitespace-nowrap">
              {tNews('comments', { count: item.commentCount })}
            </span>
          </>
        )}
      </div>
      <span className={cn(titleCls, 'transition-colors group-hover:opacity-80')}>{item.title}</span>
    </a>
  );
}

function CommentRow({ item }: { item: CommentReply }) {
  const t = useTranslations('eventComments');

  return (
    // `pl-1` leaves room for the Master ring's halo: the rail and the slider
    // viewport both clip at this edge.
    <div className={cn(rowCls, 'flex-row items-start gap-2 pl-1')}>
      <UserAvatar userId={item.outpollUserId} look={item.look} size={20} className="mt-0.5" />
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-2">
          <span className="truncate text-[12px] font-normal leading-4 text-v2-text-primary">
            <GlowName glow={item.look?.glow ?? null}>{item.author || t('anonymous')}</GlowName>
          </span>
          <RelativeDateText
            date={new Date(item.publishedAt)}
            formatStyle="compact"
            className="shrink-0 text-[10px] leading-4 text-v2-text-secondary opacity-50"
          />
        </div>
        <p className="line-clamp-2 text-[10px] leading-[14px] text-v2-text-secondary">
          {item.content}
        </p>
      </div>
    </div>
  );
}

export { EventBannerRelated };
